import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }


  /**
   * Open the MyDecoraj selector using card-embedded variant data. No product-page
   * fetch, media gallery or heavyweight product DOM is needed.
   * Native product-form-component still submits and updates the shared cart.
   * @returns {boolean}
   */
  #openFastQuickAdd() {
    const json = this.querySelector('script[data-md-quick-buy-data]');
    const content = document.getElementById('quick-add-modal-content');
    if (!json?.textContent || !content) return false;
    let data;
    try {
      data = JSON.parse(json.textContent);
    } catch (error) {
      console.warn('[MyDecoraj quick add] Invalid product data', error);
      return false;
    }
    if (!data?.variants?.length) return false;
    const variants = data.variants;
    let active = variants.find((variant) => String(variant.id) === String(data.initialVariant));
    if (!active) active = variants.find((variant) => variant.available) || variants[0];
    let chosen = Array.isArray(active.options) ? [...active.options] : [];

    // Fixed HTML only. Product values are inserted with textContent and safe attributes.
    content.innerHTML = `
      <div class="md-fast-quick">
        <div class="mdfq-head">
          <a class="mdfq-image-link" href="#"><img class="mdfq-image" alt="" width="88" height="112"></a>
          <div class="mdfq-heading">
            <span class="mdfq-kicker">CHOISISSEZ VOTRE MIROIR</span>
            <a class="mdfq-title" href="#"></a>
            <div class="mdfq-prices"><span class="mdfq-price"></span><del class="mdfq-compare" hidden></del></div>
          </div>
        </div>
        <div class="mdfq-scroll" role="group" aria-label="Options du miroir">
          <div class="mdfq-options"></div>
        </div>
        <product-form-component class="mdfq-form" data-quantity-default="1" on:submit="/handleSubmit">
          <div class="visually-hidden" aria-live="polite" aria-atomic="true" role="status" ref="liveRegion"></div>
          <form data-type="add-to-cart-form" novalidate>
            <input type="hidden" name="id" ref="variantId">
            <input type="hidden" name="quantity" value="1">
            <div class="mdfq-actions">
              <div class="mdfq-quantity" role="group" aria-label="Quantité">
                <button type="button" class="mdfq-minus" aria-label="Diminuer la quantité">−</button>
                <span class="mdfq-qty-label" aria-live="polite">1</span>
                <button type="button" class="mdfq-plus" aria-label="Augmenter la quantité">+</button>
              </div>
              <button type="submit" class="mdfq-submit">Ajouter au panier</button>
            </div>
            <p class="mdfq-status" role="status" aria-live="polite" hidden></p>
          </form>
        </product-form-component>
      </div>`;
    const headImage = content.querySelector('.mdfq-image');
    const title = content.querySelector('.mdfq-title');
    const imageLink = content.querySelector('.mdfq-image-link');
    const price = content.querySelector('.mdfq-price');
    const compare = content.querySelector('.mdfq-compare');
    const optionArea = content.querySelector('.mdfq-options');
    const form = content.querySelector('product-form-component');
    const inputId = form.querySelector('input[name="id"]');
    const inputQty = form.querySelector('input[name="quantity"]');
    const qtyLabel = content.querySelector('.mdfq-qty-label');
    const minus = content.querySelector('.mdfq-minus');
    const plus = content.querySelector('.mdfq-plus');
    const submit = content.querySelector('.mdfq-submit');
    const status = content.querySelector('.mdfq-status');
    const productUrl = new URL(data.url || this.productPageUrl, window.location.origin);
    const productLink = productUrl.pathname + productUrl.search;
    title.textContent = data.title || '';
    title.href = productLink;
    imageLink.href = productLink;
    if (data.image) {
      headImage.src = data.image;
      headImage.alt = data.title || '';
      headImage.loading = 'eager';
      headImage.decoding = 'async';
    } else {
      headImage.hidden = true;
      imageLink.hidden = true;
    }
    form.dataset.productId = String(data.id || '');
    const money = (cents) => new Intl.NumberFormat('fr-MA', { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(Number(cents || 0) / 100) + ' DH';
    const minQty = () => Math.max(1, Number(active?.min) || 1);
    const qtyStep = () => Math.max(1, Number(active?.step) || 1);
    function updateQuantity(delta) {
      const min = minQty();
      const max = active?.max == null ? Infinity : Number(active.max);
      const next = Math.max(min, Math.min(max, Number(inputQty.value || min) + delta * qtyStep()));
      inputQty.value = String(next);
      qtyLabel.textContent = String(next);
      minus.disabled = next <= min;
      plus.disabled = next + qtyStep() > max;
    }
    function updateSelection() {
      active = variants.find((variant) => variant.options?.length === chosen.length && variant.options.every((value, i) => value === chosen[i])) || null;
      inputId.value = active?.available ? String(active.id) : '';
      const available = Boolean(active?.available);
      submit.disabled = !available;
      submit.textContent = available ? 'Ajouter au panier' : 'Indisponible';
      status.hidden = available;
      status.textContent = available ? '' : 'Cette combinaison n’est pas disponible. Choisissez une autre option.';
      if (active) {
        price.textContent = money(active.price);
        const hasCompare = Number(active.compareAt) > Number(active.price);
        compare.hidden = !hasCompare;
        compare.textContent = hasCompare ? money(active.compareAt) : '';
      } else {
        price.textContent = '';
        compare.hidden = true;
      }
      inputQty.value = String(minQty());
      updateQuantity(0);
    }
    (data.options || []).forEach((option, index) => {
      if (!Array.isArray(option.values) || !option.values.length) return;
      if (option.values.length === 1 && data.options.length === 1) return;
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'mdfq-group';
      const legend = document.createElement('legend');
      legend.textContent = option.name || 'Option';
      fieldset.append(legend);
      const choices = document.createElement('div');
      choices.className = 'mdfq-pills';
      option.values.forEach((value) => {
        const label = document.createElement('label');
        label.className = 'mdfq-pill';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'mdfq-option-' + index;
        radio.value = value;
        radio.checked = chosen[index] === value;
        const labelText = document.createElement('span');
        labelText.textContent = value;
        label.append(radio, labelText);
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          chosen[index] = value;
          updateSelection();
        });
        choices.append(label);
      });
      fieldset.append(choices);
      optionArea.append(fieldset);
    });
    minus.addEventListener('click', () => updateQuantity(-1));
    plus.addEventListener('click', () => updateQuantity(1));
    updateSelection();
    this.#openQuickAddModal();
    const scroll = content.querySelector('.mdfq-scroll');
    if (scroll) scroll.scrollTop = 0;
    return true;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Fast path: render the variant selector synchronously from the product card.
    // Keep legacy product-page fetching only for unsupported/custom products.
    if (this.#openFastQuickAdd()) return;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
